"use client";

import { X, Down, Search } from "@repo/icons";
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { cn } from "@repo/cn";
import { CpsInput } from "../CpsInput";
import type {
  CpsAutocompleteListItem,
  CpsAutocompleteContextType,
  CpsAutocompleteRootProps,
  CpsAutocompleteInputProps,
  CpsAutocompleteDropdownProps,
  CpsAutocompleteEmptyProps,
  CpsAutocompleteListProps,
  CpsAutocompleteItemProps,
  CpsAutocompleteClearButtonProps,
  CpsAutocompleteProps,
} from "./cps-autocomplete.types";

function createItemsWithCustomOption(
  baseItems: CpsAutocompleteListItem[],
  query: string,
  allowCustomOptions: boolean,
): CpsAutocompleteListItem[] {
  if (!allowCustomOptions || !query.trim()) return baseItems;

  const exactMatch = baseItems.find(
    (item) => item.option.toLowerCase() === query.toLowerCase(),
  );

  if (exactMatch) return baseItems;

  const customOption: CpsAutocompleteListItem = {
    option: query,
    isCustom: true,
  };

  return [customOption, ...baseItems];
}

const AutocompleteContext = createContext<CpsAutocompleteContextType | null>(null);

const useAutocomplete = (): CpsAutocompleteContextType => {
  const context = useContext(AutocompleteContext);
  if (!context) {
    throw new Error("Componente deve ser usado dentro de CpsAutocompleteRoot");
  }
  return context;
};

/**
 * Renderiza o componente raiz do Autocomplete, responsável por gerenciar o estado,
 * contexto e comportamento dos subcomponentes do autocomplete.
 *
 * Props:
 * @param children - Elementos filhos que compõem o autocomplete (input, dropdown, lista, etc)
 * @param value - Valor atual do campo de autocomplete
 * @param onValueChange - Função chamada ao alterar o valor do campo
 * @param items - Lista de opções disponíveis para seleção
 * @param onOptionSelect - Função chamada ao selecionar uma opção
 * @param id - Identificador único para acessibilidade e controle
 * @param allowCustomOptions - Permite criar opções customizadas digitando no campo
 * @param customOptionText - Texto exibido ao criar uma opção customizada. Ex.: 'Selecionar:'
 *
 * O componente provê contexto para os subcomponentes, controla exibição do dropdown,
 * seleção, busca, loading e manipulação de opções customizadas.
 *
 * Exemplo de uso:
 * `<CpsAutocompleteRoot value={value} onValueChange={setValue} items={items}>`
 *   `<CpsAutocompleteInput />`
 *   `<CpsAutocompleteDropdown>`
 *     `<CpsAutocompleteList />`
 *   `</CpsAutocompleteDropdown>`
 * `</CpsAutocompleteRoot>`
 */

const CpsAutocompleteRoot = ({
  children,
  value,
  onValueChange,
  items,
  onOptionSelect,
  id,
  allowCustomOptions = true,
  customOptionText = "",
  minSearchLength = 1,
}: CpsAutocompleteRootProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const searchItems = useMemo(
    () => createItemsWithCustomOption(items ?? [], value, allowCustomOptions),
    [items, value, allowCustomOptions],
  );

  const onSelection = useCallback(
    (item: CpsAutocompleteListItem) => {
      onValueChange(item.option);
      setShowDropdown(false);
      setHighlightedIndex(-1);
      if (onOptionSelect) onOptionSelect(item.option, item);
    },
    [onValueChange, onOptionSelect],
  );

  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const contextValue = useMemo(
    () => ({
      showDropdown,
      setShowDropdown,
      searchItems,
      highlightedIndex,
      setHighlightedIndex,
      loading,
      setLoading,
      value,
      onValueChange,
      onSelection,
      containerRef,
      inputRef,
      id,
      allowCustomOptions,
      customOptionText,
      minSearchLength,
    }),
    [
      showDropdown,
      searchItems,
      highlightedIndex,
      loading,
      value,
      allowCustomOptions,
      customOptionText,
      id,
      onValueChange,
      onSelection,
      minSearchLength,
    ],
  );

  return (
    <AutocompleteContext.Provider value={contextValue}>
      <div ref={containerRef} className="relative w-full">
        <div className={showDropdown ? "relative z-50" : "relative z-0"}>{children}</div>
      </div>
    </AutocompleteContext.Provider>
  );
};

/**
 * Campo de entrada do Autocomplete, responsável por capturar o texto digitado,
 * disparar buscas e controlar o estado de exibição do dropdown.
 *
 * Props:
 * @param IconLeft - Ícone exibido à esquerda do campo
 * @param title - Título do campo (opcional)
 * @param props - Props adicionais do input passados do formulário compatível
 *
 * Utiliza contexto do Autocomplete para manipular estado e exibir sugestões.
 */

const CpsAutocompleteInput = forwardRef<HTMLInputElement, CpsAutocompleteInputProps>(
  ({ IconLeft = Search, onSearch, title, ...props }, ref) => {
    const {
      showDropdown,
      setShowDropdown,
      value,
      onValueChange,
      setHighlightedIndex,
      inputRef,
      id,
      highlightedIndex,
      minSearchLength = 1,
    } = useAutocomplete();

    const handleInputClick = () => {
      setShowDropdown(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      onValueChange(query);
      if (query.length > minSearchLength && onSearch) onSearch(query);
      props.onChange?.(e);
      setShowDropdown(true);
      setHighlightedIndex(-1);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
        e.preventDefault();
      }
      props.onKeyDown?.(e);
    };

    return (
      <CpsInput
        ref={ref ?? (inputRef as React.Ref<HTMLInputElement>)}
        name="autocomplete"
        type="text"
        title={title}
        onClick={handleInputClick}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        value={value}
        autoComplete="off"
        id={id}
        IconLeft={IconLeft}
        IconRight={!value ? Down : undefined}
        borderColor={showDropdown ? "primary" : "neutral"}
        className={showDropdown ? "ring-2 ring-primary-400 shadow-lg" : ""}
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls={id ? `${id}-autocomplete-list` : undefined}
        aria-activedescendant={
          highlightedIndex >= 0 && id ? `${id}-option-${highlightedIndex}` : undefined
        }
        aria-labelledby={id ? `${id}-label` : undefined}
        {...props}
      />
    );
  },
);

CpsAutocompleteInput.displayName = "CpsAutocompleteInput";

/**
 * Dropdown do Autocomplete, responsável por exibir a lista de sugestões.
 *
 * Props:
 * @param children - Elementos a serem renderizados dentro do dropdown (normalmente a lista de opções)
 * @param className - Classe CSS adicional para customização
 *
 * Só é exibido quando o dropdown está aberto.
 */

const CpsAutocompleteDropdown = ({
  children,
  className = "",
}: CpsAutocompleteDropdownProps) => {
  const { showDropdown } = useAutocomplete();

  if (!showDropdown) return null;

  return (
    <div className="absolute left-0 top-full mt-2 w-full z-50">
      <div
        className={cn(
          "rounded-2xl pt-2 pl-2 pr-2 overflow-x-hidden overflow-y-auto bg-white shadow-lg max-h-72 border border-neutral-200",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
};

/**
 * Lista de opções do Autocomplete, renderiza cada sugestão disponível.
 *
 * Props:
 * @param children - Elementos customizados para renderizar opções
 * @param renderOption - Função para customizar o conteúdo de cada opção
 *
 * Utiliza contexto para acessar itens filtrados e estado de loading.
 */

const CpsAutocompleteList = ({ children, renderOption }: CpsAutocompleteListProps) => {
  const { searchItems, loading, id } = useAutocomplete();

  if (loading) return <CpsAutocompleteLoading />;

  return (
    <ul
      id={id ? `${id}-autocomplete-list` : undefined}
      className={cn(
        "flex flex-col gap-0 list-none p-0 m-0 w-full divide-y divide-neutral-100",
        searchItems.length > 0 && "pb-3",
      )}
    >
      {searchItems.length === 0 ? (
        <CpsAutocompleteEmpty />
      ) : (
        (children ??
        searchItems.map((item, idx) => (
          <CpsAutocompleteItem
            key={item.id}
            item={item}
            index={idx}
            renderOption={renderOption}
          />
        )))
      )}
    </ul>
  );
};

/**
 * Item individual da lista de autocomplete, responsável por exibir uma opção e
 * controlar seleção e destaque.
 *
 * Props:
 * @param item - Objeto da opção a ser exibida
 * @param index - Índice da opção na lista
 * @param renderOption - Função para customizar o conteúdo da opção
 *
 * Utiliza contexto para manipular seleção e destaque.
 */

const CpsAutocompleteItem = ({ item, index, renderOption }: CpsAutocompleteItemProps) => {
  const { highlightedIndex, setHighlightedIndex, onSelection, id, customOptionText } =
    useAutocomplete();

  const isHighlighted = highlightedIndex === index;

  const handleClick = () => {
    onSelection(item);
  };

  const handleMouseEnter = () => {
    setHighlightedIndex(index);
  };

  let content: React.ReactNode;
  if (item.isCustom) {
    content = (
      <div className="flex items-center">
        {customOptionText !== "" && (
          <span className="text-sm text-neutral-500 mr-2">{customOptionText}</span>
        )}
        <span className="text-md font-semibold text-primary-600">
          &quot;{item.option}&quot;
        </span>
      </div>
    );
  } else if (renderOption) {
    content = renderOption(item, index);
  } else {
    content = <DefaultItemContent item={item} customOptionText={customOptionText} />;
  }

  return (
    <button
      type="button"
      id={id ? `${id}-option-${index}` : undefined}
      className={cn(
        "flex items-center px-4 py-2 w-full text-left cursor-pointer hover:bg-primary-50 min-h-[44px]",
        { "bg-primary-50": isHighlighted },
      )}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      aria-pressed={isHighlighted}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelection(item);
        }
      }}
    >
      {content}
    </button>
  );
};

/**
 * Botão para limpar o campo de autocomplete, exibido quando há valor preenchido.
 *
 * Props:
 * @param className - Classe CSS adicional
 * @param icon - Ícone customizado para o botão
 * @param aria-label - Label de acessibilidade
 * @param onClear - Função chamada ao limpar o campo
 *
 * Utiliza contexto para manipular valor e foco do input.
 */

const CpsAutocompleteClearButton = ({
  className = "",
  icon: Icon = X,
  "aria-label": ariaLabel = "Limpar campo",
  onClear,
}: CpsAutocompleteClearButtonProps) => {
  const { value, onValueChange, inputRef, setShowDropdown } = useAutocomplete();

  if (!value) return null;

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    onValueChange("");
    setShowDropdown(false);
    inputRef.current?.focus();
    if (onClear) onClear();
  };

  return (
    <button
      type="button"
      className={cn(
        "absolute right-4 top-2/3 -translate-y-1/2 p-1 rounded-full hover:bg-neutral-100 transition z-2",
        className,
      )}
      onClick={handleClear}
      aria-label={ariaLabel}
    >
      <Icon size={16} className="fill-neutral-400" />
    </button>
  );
};

/**
 * Componente exibido quando não há resultados na busca do autocomplete.
 *
 * Props:
 * @param text - Texto exibido quando não há resultados
 * @param children - Elemento customizado para exibir no lugar do texto padrão
 *
 * Só é exibido quando não há itens e não está carregando.
 */

const CpsAutocompleteEmpty = ({
  text = "Nenhum resultado encontrado",
  children,
}: CpsAutocompleteEmptyProps) => {
  const { searchItems, loading } = useAutocomplete();

  if (loading || searchItems.length > 0) return null;

  return (
    <div className="flex items-center px-4 py-2">
      {children ?? <p className="text-base font-semibold text-neutral-300">{text}</p>}
    </div>
  );
};

/**
 * Componente exibido durante o carregamento das opções do autocomplete.
 *
 * Não recebe props. Utiliza contexto para saber se está carregando.
 */

const CpsAutocompleteLoading = () => {
  const { loading } = useAutocomplete();

  if (!loading) return null;

  return (
    <div className="flex items-center justify-center py-4 text-neutral-400">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mr-2" />
      Carregando...
    </div>
  );
};

const DefaultItemContent = ({
  item,
  customOptionText,
}: {
  item: CpsAutocompleteListItem;
  customOptionText: string;
}) => (
  <div className="flex items-center w-full">
    {item.Icon ? (
      <div className="w-10 h-10 bg-primary-50 flex shrink-0 items-center justify-center rounded-full mr-3">
        <item.Icon size={24} className="fill-primary-400" />
      </div>
    ) : null}
    <div className="flex-1">
      {item.isCustom ? (
        <div className="flex items-center">
          <span className="text-sm text-neutral-500 mr-2">{customOptionText}</span>
          <span className="text-md font-semibold text-primary-600">
            &quot;{item.option}&quot;
          </span>
        </div>
      ) : (
        <span className="text-md font-semibold text-neutral-700">{item.option}</span>
      )}
    </div>
  </div>
);

export {
  CpsAutocompleteRoot,
  CpsAutocompleteInput,
  CpsAutocompleteDropdown,
  CpsAutocompleteLoading,
  CpsAutocompleteEmpty,
  CpsAutocompleteList,
  CpsAutocompleteItem,
  CpsAutocompleteClearButton,
};

export const CpsAutocompleteLegacy = forwardRef<HTMLInputElement, CpsAutocompleteProps>(
  (
    {
      id,
      value,
      onValueChange,
      IconLeft,
      items,
      renderOption,
      onOptionSelect,
      allowCustomOptions = true,
      customOptionText = "Selecionar:",
      ...props
    },
    ref,
  ) => {
    return (
      <CpsAutocompleteRoot
        value={value}
        onValueChange={onValueChange}
        items={items ?? []}
        onOptionSelect={onOptionSelect}
        id={id}
        allowCustomOptions={allowCustomOptions}
        customOptionText={customOptionText}
      >
        <CpsAutocompleteInput ref={ref} IconLeft={IconLeft} {...props} />
        <CpsAutocompleteDropdown>
          <CpsAutocompleteList renderOption={renderOption} />
        </CpsAutocompleteDropdown>
      </CpsAutocompleteRoot>
    );
  },
);

CpsAutocompleteLegacy.displayName = "CpsAutocompleteLegacy";
